import { getSharedState } from "./windowBus";

// Framework-agnostic theme storage/broadcast, split out of theme.ts so it
// carries no dependency on dom.ts. theme.ts (vanilla) still owns wiring the
// toggle button; React's useColorScheme hook imports only this module -
// importing theme.ts instead would transitively pull in dom.ts's entire
// side-effecting module graph (~140 requireEl() calls) into the React
// bundle.
const STORAGE_KEY = "gallery-theme";
export type Theme = "light" | "dark";

function storedOverride(): Theme | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" ? v : null;
}

export function getTheme(): Theme {
  // Dark mode is opt-in only (see styles.css) - the app always defaults to
  // light unless the user has explicitly toggled it before.
  return storedOverride() || "light";
}

// localStorage/data-theme reads above are genuinely shared (same origin,
// same document) - only the listener notification needs windowBus, since
// app.js and react-app.js each get their own private copy of a plain
// module-level Set (see windowBus.ts).
function listeners(): Set<(theme: Theme) => void> {
  return getSharedState("__themeListeners", () => new Set<(theme: Theme) => void>());
}

export function onThemeChange(fn: (theme: Theme) => void): () => void {
  listeners().add(fn);
  return () => listeners().delete(fn);
}

// Sole write path for theme changes - updates storage + the data-theme
// attribute both worlds read, then broadcasts to subscribers.
export function setTheme(next: Theme): void {
  localStorage.setItem(STORAGE_KEY, next);
  document.documentElement.setAttribute("data-theme", next);
  listeners().forEach((fn) => fn(next));
}
